/**
 * sensitiveDataCheck.js
 * Detects card numbers, UK account numbers, sort codes, and IBANs in free-text
 * fields. Used to hard-block accidental entry of sensitive financial identifiers.
 *
 * Approach: normalise separators first (strip spaces and hyphens), then match
 * clean digit runs by length. Avoids \b word-boundary fragility with optional
 * separators in the middle of patterns.
 */

function luhn(digits) {
  let sum = 0
  let alt = false
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i], 10)
    if (alt) { n *= 2; if (n > 9) n -= 9 }
    sum += n
    alt = !alt
  }
  return sum % 10 === 0
}

/**
 * Returns true if the string appears to contain a card number,
 * UK bank account number, sort code, or IBAN.
 */
export function containsSensitiveData(value) {
  if (!value || typeof value !== 'string') return false
  const v = value.trim()
  if (!v) return false

  // UK IBAN - check before normalisation (letters are significant)
  if (/GB\d{2}[A-Z0-9]{18}/i.test(v)) return true

  // Sort code XX-XX-XX - check before normalisation (dashes are the pattern)
  if (/\d{2}-\d{2}-\d{2}/.test(v)) return true

  // Normalise: remove spaces and hyphens so any card format collapses to digits
  // "4111 1111 1111 1111" → "4111111111111111"
  // "4111-1111-1111-1111" → "4111111111111111"
  const normalised = v.replace(/[\s-]/g, '')

  // Extract all maximal runs of consecutive digits
  const runs = normalised.match(/\d+/g) || []

  for (const run of runs) {
    // 16-digit card (Visa / Mastercard / others) - Luhn validated
    if (run.length === 16 && luhn(run)) return true

    // 15-digit card (Amex) - Luhn validated
    if (run.length === 15 && luhn(run)) return true

    // UK bank account number - 8 digits
    if (run.length === 8) return true
  }

  return false
}

export const SENSITIVE_DATA_ERROR =
  "This looks like it might contain a card or account number - for your security we can't save this."
